import * as React from "react";
import { Check, ChevronsUpDown, Plus, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import { Badge } from "./badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover";

/**
 * SearchableSelect - A searchable dropdown with optional quick-add functionality
 * 
 * @param {Array} options - Array of {value, label} objects
 * @param {string} value - Currently selected value
 * @param {function} onValueChange - Callback when value changes
 * @param {string} placeholder - Placeholder text
 * @param {string} searchPlaceholder - Search input placeholder
 * @param {boolean} disabled - Whether the select is disabled
 * @param {function} onQuickAdd - Callback for quick add button (shows + button if provided)
 * @param {string} quickAddLabel - Label for quick add button tooltip
 * @param {string} emptyText - Text to show when no results found
 * @param {string} className - Additional className for the trigger
 */
const SearchableSelect = React.forwardRef(({
  options = [],
  value,
  onValueChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  disabled = false,
  onQuickAdd,
  quickAddLabel = "Add new",
  emptyText = "No results found.",
  className,
  ...props
}, ref) => {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");

  const selectedOption = options.find((opt) => opt.value === value);

  const filteredOptions = React.useMemo(() => {
    if (!searchQuery) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((opt) => 
      opt.label?.toLowerCase().includes(query) ||
      opt.value?.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  return (
    <div className="flex gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              "w-full justify-between font-normal",
              !value && "text-muted-foreground",
              className
            )}
            {...props}
          >
            <span className="truncate">
              {selectedOption?.label || placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={false}>
            <div className="flex items-center border-b px-3">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <input
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup className="max-h-[200px] overflow-y-auto">
                {filteredOptions.map((option, index) => (
                  <CommandItem
                    key={option.key || `${option.value}-${index}`}
                    value={option.value}
                    onSelect={() => {
                      onValueChange(option.value === value ? "" : option.value);
                      setOpen(false);
                      setSearchQuery("");
                    }}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === option.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      
      {onQuickAdd && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={onQuickAdd}
          disabled={disabled}
          title={quickAddLabel}
          className="shrink-0"
        >
          <Plus className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
});

SearchableSelect.displayName = "SearchableSelect";

/**
 * MultiSearchableSelect - Multi-select variant with chips for selected items.
 * Same `{value, label}` option shape as SearchableSelect.
 *
 * @param {string[]} value - Array of currently selected values
 * @param {function} onValueChange - Callback receiving the new array
 */
const MultiSearchableSelect = React.forwardRef(({
  options = [],
  value = [],
  onValueChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  disabled = false,
  emptyText = "No results found.",
  className,
  ...props
}, ref) => {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");

  const selectedSet = new Set(value || []);
  const selectedOptions = options.filter((o) => selectedSet.has(o.value));

  const toggle = (v) => {
    const cur = value || [];
    if (cur.includes(v)) onValueChange(cur.filter((x) => x !== v));
    else onValueChange([...cur, v]);
  };

  const filteredOptions = React.useMemo(() => {
    if (!searchQuery) return options;
    const q = searchQuery.toLowerCase();
    return options.filter((o) =>
      o.label?.toLowerCase().includes(q) || o.value?.toLowerCase().includes(q)
    );
  }, [options, searchQuery]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal h-auto min-h-9 py-1.5",
            className
          )}
          {...props}
        >
          {selectedOptions.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {selectedOptions.map((o) => (
                <Badge key={o.value} variant="secondary" className="gap-1 pr-1">
                  <span className="truncate max-w-[140px]">{o.label}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(o.value);
                    }}
                    className="rounded hover:bg-muted p-0.5"
                    aria-label={`Remove ${o.label}`}
                  >
                    <X className="h-3 w-3" />
                  </span>
                </Badge>
              ))}
            </div>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup className="max-h-[220px] overflow-y-auto">
              {filteredOptions.map((option, index) => {
                const checked = selectedSet.has(option.value);
                return (
                  <CommandItem
                    key={option.key || `${option.value}-${index}`}
                    value={option.value}
                    onSelect={() => toggle(option.value)}
                    className="cursor-pointer"
                  >
                    <Check className={cn("mr-2 h-4 w-4", checked ? "opacity-100" : "opacity-0")} />
                    {option.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
});

MultiSearchableSelect.displayName = "MultiSearchableSelect";

export { SearchableSelect, MultiSearchableSelect };
